import type { User as FirebaseUser } from "firebase/auth";
import { api } from "../api";
import { signInToFirebaseWithCustomToken } from "../firebase";
import { retryWithBackoff } from "../retry";

/**
 * Exchange a native-session token for a Firebase custom token and sign in.
 *
 * Never throws — resolves to the Firebase user, or null when the exchange
 * fails, so bridge callbacks that fire this in the background can't leak
 * unhandled rejections. Callers decide whether to surface an error state.
 *
 * The /user/auth/token fetch is retried with backoff — transient WebView
 * network drops are the dominant failure mode. If Firebase rejects the custom
 * token itself (they expire after an hour, and a suspended WebView can replay
 * a stale one on resume) a fresh token is fetched and tried once more.
 */
export async function signInFromNativeAuthToken(
  token: string,
): Promise<FirebaseUser | null> {
  const fetchCustomToken = () =>
    retryWithBackoff(
      () =>
        api.get<{ token: string }>("/user/auth/token", undefined, async () => token),
      { attempts: 3, baseDelayMs: 1000 },
    );

  try {
    const response = await fetchCustomToken();
    if (!response.success || !response.data?.token) return null;

    try {
      return await signInToFirebaseWithCustomToken(response.data.token);
    } catch (e) {
      const code = (e as { code?: string })?.code;
      if (
        code !== "auth/invalid-custom-token" &&
        code !== "auth/network-request-failed"
      ) {
        throw e;
      }
      const fresh = await fetchCustomToken();
      if (!fresh.success || !fresh.data?.token) return null;
      return await signInToFirebaseWithCustomToken(fresh.data.token);
    }
  } catch (e) {
    console.error("[AuthProvider] Native token sign-in failed:", e);
    return null;
  }
}
